// Helpers for building system component demos.
import { StyleSheet, Text, View } from "react-native";
import { Center, FlowBox, Pad, PadBox, VertBox } from "component/basics";
import { Catcher } from "./catcher";
import { Tag } from "component/button";
import { TestState } from "util/datastore";
import { fontFamilySansRegular, Heading } from "component/text";
import React from "react";
import { colorBorderPrimary, colorTextPrimary } from "component/color";
import { getFontSize } from "platform-specific/font";
import { TLabel } from "util/stdtypes";

export type DemoFileConfig = {    
    designUrl?: string;
}

const global_demo_files : DemoFile[] = [];

export function getDemoFiles() {
    return global_demo_files;
}

export type DemoFile = {
    path: string,
    designUrl?: string,
    componentDemos: ComponentDemo[],
    featureDemo?: FeatureDemo,
}

let global_temp_path = '';
let global_temp_design_url = undefined as string | undefined;

export function demoFile(path: string, config?: DemoFileConfig) {
    global_temp_path = path;
    global_temp_design_url = config?.designUrl;
}

export type DemoAction = {
    action: 'click' | 'input' | 'hover' | 'within' | 'popup' | 'popup-close' | 'check_text' | 'key_press',
    matcher?: string,
    text?: string,
    innerAction?: DemoAction,
    popupAction?: DemoAction,
    key?: {keyCode: number, ctrlKey?: boolean, shiftKey?: boolean, altKey?: boolean},
    expectedText?: string,
}

export type DemoStory = {
    name: string,
    key?: string,
    actions: DemoAction[],
}

export type DemoVariant<T> = {
    name: string | null,
    props: VariantProps<T>,
    stories?: DemoStory[],
}

export type VariantProps<T> = {
    args?: T,
    description?: string,
    state?: TestState,
    features?: string[],
    config?: any,
    siloKey?: string,
    structureKey?: string,
    instanceKey?: string,
}

export type ComponentProps<T> = VariantProps<T>

export type ComponentDemo = {
    name: string,
    component: React.ComponentType<any>,
    variants: DemoVariant<any>[],
    props: ComponentProps<any>
}

function getOrCreateActiveDemo() {
    let activeDemo = global_demo_files[global_demo_files.length - 1];
    if (!activeDemo || activeDemo?.path !== global_temp_path) {
        activeDemo = {
            path: global_temp_path,
            designUrl: global_temp_design_url,
            componentDemos: [],
            featureDemo: undefined
        }
        global_demo_files.push(activeDemo);
    }
    return activeDemo;
}

export function component<T>(name: string, reactComponent: React.ComponentType<T>, 
    props: ComponentProps<T> = {}, stories: DemoStory[] = []
) {
    const activeDemo = getOrCreateActiveDemo();
    
    const defaultVariant = variant(null, {}, stories);
    activeDemo.componentDemos.push({
        name,
        component: reactComponent,
        variants: [defaultVariant],
        props
    });
}

export function componentWithVariants<T>(name: string, reactComponent: React.ComponentType<T>, 
    props: ComponentProps<T> = {}, variants: DemoVariant<T>[] = [variant(null)]
) {
    const activeDemo = getOrCreateActiveDemo();
    
    activeDemo.componentDemos.push({
        name,
        component: reactComponent,
        variants,
        props,
    });
}

export function extend<T>(componentName: string, extensionName: string, componentDemoed: React.ComponentType<T>, 
    props: ComponentProps<T> = {}, stories: DemoStory[] = []
) {
    component(componentName + ' (' + extensionName + ')', componentDemoed, props, stories)
}

export function extendWithVariants<T>(componentName: string, extensionName: string, componentDemoed: React.ComponentType<T>, 
    props: ComponentProps<T> = {}, variants: DemoVariant<T>[] = [variant(null)]
) {
    componentWithVariants(componentName + ' (' + extensionName + ')', componentDemoed, props, variants)
}



export function variant<T>(name: string | null, props?: VariantProps<T>, stories?: DemoStory[]) : DemoVariant<T> {
    return {
        name,
        props: props || {},
        stories
    }
}


export function story(name: string, actions: DemoAction[]): DemoStory {
    return {
        name,
        key: name.toLowerCase().replace(/ /g, '-'),
        actions
    }
}

export type FeatureDemo = {
    description?: string,
    state?: TestState,
    features?: Record<string, boolean>,
    config?: any,
    siloKey?: string,
    structureKey?: string,
    instanceKey?: string,
}

export function feature(featureKey: string, props: FeatureDemo) {
    let activeDemo = global_demo_files[global_demo_files.length - 1];
    if (!activeDemo || activeDemo?.path !== global_temp_path) {
        activeDemo = {
            path: global_temp_path,
            designUrl: global_temp_design_url,
            componentDemos: []
        }
        global_demo_files.push(activeDemo);
    }
    activeDemo.featureDemo = {
        ...props,
        features: {
            ...(props.features ?? {}),
            [featureKey]: true
        }
    }
}

export function CLICK(matcher) : DemoAction {
    return {matcher, action: 'click'}
}

export function HOVER(matcher) : DemoAction {
    return {matcher, action: 'hover'}
}

export function INPUT(matcher, text) : DemoAction {
    return {matcher, action: 'input', text}
}

export function WITHIN(matcher, action) : DemoAction {
    return {matcher, action: 'within', innerAction: action}
}

export function POPUP(popupAction) : DemoAction{
    return {action: 'popup', popupAction}
}

export function POPUP_CLOSE() : DemoAction {
    return {action: 'popup-close'}
}

export function CHECK_TEXT(expectedText) : DemoAction {
    return { action: 'check_text', expectedText };
}

export function KEY_PRESS({keyCode, ctrlKey=false, shiftKey=false, altKey=false}) : DemoAction {
    return { action: 'key_press', key: {keyCode, ctrlKey, shiftKey, altKey} };
}


export function DemoBox({gap=16, wide=true, flow=false, children}) {
    if (flow) {
        return <FlowBox rowGap={16} columnGap={16}>{children}</FlowBox>
    }
    return <VertBox wide={wide} gap={gap}>{children}</VertBox>
}

export function DemoSection({text, horiz=false, mini=false, children, color=null}) {
    const s = DemoSectionStyle;
    return <View style={{marginBottom: 32}}>
        <Heading text={text} />
        <Pad size={8} />
        <View style={[mini ? s.boxMini : s.box, color && {backgroundColor: color}]}>
            <Catcher>
                <SpacedArray horiz={horiz}>{children}</SpacedArray>
            </Catcher>
        </View>
    </View>
}
const DemoSectionStyle = StyleSheet.create({
    box: {
        borderColor: colorBorderPrimary,
        borderWidth: 1,
        borderRadius: 12,
        padding: 20,
        paddingBottom: 0
    },
    boxMini: {
        padding: 20,
        paddingBottom: 0
    }

});

export function DemoHeader({label, text}) {
    const s = DemoHeaderStyle;
    return <Text style={s.header}>{label || text}</Text>
}
const DemoHeaderStyle = StyleSheet.create({
    header: {
        fontFamily: fontFamilySansRegular,
        fontSize: getFontSize(24),
        lineHeight: 32,
        color: colorTextPrimary
    }
})

export function SpacedArray({pad=20, horiz=false, children}) {
    if (children.length > 1) {
        return <View style={horiz ? {flexDirection: 'row', flexWrap: horiz ? 'wrap' : undefined} : null} >
            {children.map((c, i) => 
                <PadBox key={i} right={horiz ? pad : 0} bottom={pad}><Catcher>{c}</Catcher></PadBox>
            )}
        </View>
    } else {
        return <View>
            {children}
            <Pad size={pad} />
        </View>
    }
}

export function DemoWidget({color=undefined, text}) {
    return <PadBox bottom={10}><Tag color={color} text={text} textColor={colorTextPrimary} /></PadBox>
}

export function DemoPageWidget({text}) {
    return <PadBox bottom={10} top={20}>
        <Center>
            <Tag text={text} textColor={colorTextPrimary}/>
        </Center>
    </PadBox>
}

export type ComponentDemoConfig = {
    componentSections: DemoSection[];
    structureSections: DemoSection[];
    featureSections: DemoSection[];
    openLinksInNewTab: boolean;
};

export type DemoSection = {
    name: string;
    key: string;
    designUrl?: string;
    pages: DemoPage[];
};

export type DemoPage = {
    name: string;
    key: string;
    designUrl?: string;
    storySets?: () => DemoStorySetType[];
    storySetList?: DemoStorySetType[];
    screen?: React.ComponentType<{}>;
};

export type DemoStorySetType = TestState & {
    name: string;
    key?: string;
    siloKey?: string;
    structureKey?: string;
    instanceKey?: string;
    screenKey?: string;
    params?: Record<string, any>;
    config?: any;
    pad?: boolean;
    features?: string[];
    content: React.ReactNode;
    stories?: DemoStory[];
};